/* Traps Tab/ArrowUp/ArrowDown focus within feed item rows inside a container ref.
   First Tab press focuses the first item; Tab and arrows cycle through items. */

import { useEffect, useRef, useCallback } from "react";

export function useFeedListKeyboard() {
  const listRef = useRef<HTMLDivElement>(null);

  const getFeedItems = useCallback(() => {
    if (!listRef.current) return [];
    return Array.from(
      listRef.current.querySelectorAll<HTMLElement>("[tabindex='0']"),
    );
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isTab = e.key === "Tab";
      const isArrowDown = e.key === "ArrowDown";
      const isArrowUp = e.key === "ArrowUp";

      if (!isTab && !isArrowDown && !isArrowUp) return;

      const items = getFeedItems();
      if (items.length === 0) return;

      const currentIndex = items.indexOf(
        document.activeElement as HTMLElement,
      );

      e.preventDefault();

      if (currentIndex === -1) {
        items[0].focus();
        return;
      }

      const goBack = (isTab && e.shiftKey) || isArrowUp;

      if (goBack) {
        const prevIndex =
          currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
        items[prevIndex].focus();
      } else {
        const nextIndex =
          currentIndex >= items.length - 1 ? 0 : currentIndex + 1;
        items[nextIndex].focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [getFeedItems]);

  return listRef;
}
